import { FileText, Calculator, BookOpen, ClipboardCheck, Lock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { PageHeader, CtaBand, SectionHeading } from "@/components/sections";

const PLANNED = [
  { icon: BookOpen, title: "Accounting & Tax Guides", desc: "Clear, practical guides on bookkeeping, tax and financial administration for SA businesses." },
  { icon: FileText, title: "Business Finance Articles", desc: "Focused articles on managing and understanding your business finances." },
  { icon: ClipboardCheck, title: "Financial Checklists", desc: "Simple checklists to help you stay organised and compliant." },
];

const TOOLS = [
  { icon: Calculator, title: "Business Accounting Needs Assessment", desc: "A guided assessment giving a high-level indication of the accounting services that may be relevant to you.", live: true, to: "/needs-assessment" },
  { icon: Calculator, title: "Tax Estimator", desc: "Indicative tax estimation tool (planned — will only be released once formulas are independently validated)." },
  { icon: Calculator, title: "VAT Assessment", desc: "A quick guide to whether VAT may apply to your business (planned)." },
];

export default function Resources() {
  return (
    <>
      <Seo
        title="Resources & Financial Tools"
        description="Bonsai Financial resources: accounting and tax guides, business finance articles, financial checklists, and planned digital financial tools for South African businesses."
        path="/resources"
      />
      <PageHeader
        eyebrow="Resources"
        title="Helpful financial resources — quality over volume."
        intro="We're building a focused library of genuinely useful guides, checklists and tools. Rather than publishing generic content, we add resources that help South African individuals and businesses make better financial decisions."
      />

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Coming Soon" title="Guides & articles" intro="These resources are in preparation and will be published here." />
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {PLANNED.map((r, i) => (
              <div key={r.title} data-testid={`resource-card-${i}`} className="rounded-2xl border border-slate-200/80 bg-white p-7">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#133827]/8 text-[#2C5E43]">
                  <r.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-[#18201B]">{r.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#526057]">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Financial Tools" title="Digital tools (in development)" intro="Our priority tool is the Business Accounting Needs Assessment. Tools involving tax calculations will only be released once the formulas and requirements have been independently validated." />
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {TOOLS.map((t, i) => (
              <div
                key={t.title}
                data-testid={`tool-card-${i}`}
                className={`relative rounded-2xl border p-7 ${t.live ? "border-[#C68B59] bg-[#C68B59]/5" : "border-slate-200/80 bg-[#F9F8F5]"}`}
              >
                {t.live && (
                  <span className="absolute right-4 top-4 rounded-full bg-[#25D366] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">Available Now</span>
                )}
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#133827] text-[#C68B59]">
                  <t.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-[#18201B]">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#526057]">{t.desc}</p>
                {t.live ? (
                  <Link to={t.to} data-testid={`tool-start-${i}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2C5E43] hover:text-[#133827]">
                    Start the tool <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#526057]"><Lock className="h-3.5 w-3.5" /> In development</p>
                )}
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-3xl rounded-xl bg-[#F9F8F5] p-4 text-xs leading-relaxed text-[#526057]">
            Note: Any future tool results are indicative only and should be discussed with Bonsai
            Financial before any decision is made. Bonsai Financial does not provide automated
            regulated financial advice.
          </p>
        </div>
      </section>

      <CtaBand from="resources-cta" title="Prefer to just speak to someone?" />
    </>
  );
}
